import {
  TRANSFERENCIA_LOTE_LIMITE_SINCRONO,
  type ResultadoTransferenciaLote,
} from '@baiocchi/shared'
import { Alerta, AreaTexto, Botao, Campo, ContadorTexto, Modal, Selecao } from '@baiocchi/ui'
import { useState } from 'react'

import { leadsApi } from '../../api/leads'
import { ErroApi } from '../../api/erros'
import {
  opcoesDePessoas,
  opcoesDeReferencia,
  useAtendentesElegiveis,
  useEquipes,
} from '../../ganchos/usar-catalogos'

export interface PropriedadesTransferenciaLote {
  modo: 'transferir' | 'equipe' | null
  leadUuids: ReadonlyArray<string>
  onFechar: () => void
  onConcluido: (resultado: ResultadoTransferenciaLote) => void
}

/**
 * TELA-26 — transferência em lote a partir da seleção da TELA-04.
 *
 * Três coisas que a tela precisa deixar explícitas, porque a API as impõe:
 *
 *  1. **Motivo é obrigatório** (RN-26), com mínimo de 10 caracteres. Não é
 *     cerimônia: a transferência gera uma interação na timeline, que é
 *     append-only, e "ok" gravado em 200 leads não pode ser corrigido depois.
 *  2. **Acima de 200 leads a operação é recusada**, não fatiada
 *     (`422 LOTE_ACIMA_DO_LIMITE_SINCRONO`). A fila não existe ainda; transferir os
 *     primeiros 200 e responder `200` faria o gestor acreditar que o lote inteiro
 *     foi aplicado.
 *  3. **Lead inválido é ignorado com motivo**, e o resto do lote é aplicado. O
 *     resultado mostra os ignorados — abortar 199 leads válidos por causa de um
 *     encerrado transformaria a operação em tentativa e erro.
 */
export function ModalTransferenciaLote({
  modo,
  leadUuids,
  onFechar,
  onConcluido,
}: PropriedadesTransferenciaLote) {
  const [equipeId, setEquipeId] = useState('')
  const [responsavelUuid, setResponsavelUuid] = useState('')
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const equipes = useEquipes()
  const atendentes = useAtendentesElegiveis(equipeId ? Number(equipeId) : null)

  const acimaDoLimite = leadUuids.length > TRANSFERENCIA_LOTE_LIMITE_SINCRONO
  const ehEquipe = modo === 'equipe'

  const podeEnviar =
    !acimaDoLimite &&
    motivo.trim().length >= 10 &&
    (ehEquipe ? equipeId !== '' : responsavelUuid !== '' || equipeId !== '')

  function limpar() {
    setEquipeId('')
    setResponsavelUuid('')
    setMotivo('')
    setErro(null)
  }

  async function submeter() {
    setErro(null)
    setEnviando(true)
    try {
      const corpo = {
        leadUuids: [...leadUuids],
        motivo: motivo.trim(),
        ...(equipeId ? { equipeId: Number(equipeId) } : {}),
        ...(responsavelUuid ? { responsavelUuid } : {}),
      }
      const resultado = ehEquipe
        ? await leadsApi.mudarEquipeLote(corpo)
        : await leadsApi.transferirLote(corpo)
      limpar()
      onConcluido(resultado)
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível concluir a operação.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Modal
      aberto={modo !== null}
      onFechar={() => {
        limpar()
        onFechar()
      }}
      travado={enviando}
      titulo={ehEquipe ? 'Mudar leads de equipe' : 'Transferir responsável'}
      descricao={`${leadUuids.length} lead(s) selecionado(s).`}
      rodape={
        <>
          <Botao
            variante="contorno"
            onClick={() => {
              limpar()
              onFechar()
            }}
            disabled={enviando}
          >
            Cancelar
          </Botao>
          <Botao onClick={() => void submeter()} carregando={enviando} disabled={!podeEnviar}>
            Confirmar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Alerta tom="perigo">{erro}</Alerta> : null}

        {acimaDoLimite ? (
          <Alerta tom="atencao" titulo="Seleção acima do limite">
            A operação síncrona aceita até {TRANSFERENCIA_LOTE_LIMITE_SINCRONO} leads. Reduza a
            seleção — o processamento em fila, com relatório, ainda não está disponível.
          </Alerta>
        ) : null}

        <Campo
          rotulo="Nova equipe"
          obrigatorio={ehEquipe}
          dica={
            ehEquipe
              ? 'O responsável que não pertencer à equipe de destino volta ao pool da equipe.'
              : 'Opcional. Mudar a equipe também move a unidade do lead.'
          }
        >
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio="Manter a equipe atual"
              value={equipeId}
              opcoes={opcoesDeReferencia(equipes.data?.dados)}
              onChange={(evento) => {
                setEquipeId(evento.target.value)
                setResponsavelUuid('')
              }}
            />
          )}
        </Campo>

        {!ehEquipe && (
          <Campo
            rotulo="Novo responsável"
            dica={
              equipeId
                ? 'Somente atendentes ativos e membros da equipe escolhida.'
                : 'Escolha uma equipe para carregar os atendentes elegíveis, ou deixe vazio para devolver ao pool.'
            }
          >
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Devolver ao pool (sem responsável)"
                value={responsavelUuid}
                disabled={!equipeId}
                opcoes={opcoesDePessoas(atendentes.data?.dados)}
                onChange={(evento) => setResponsavelUuid(evento.target.value)}
              />
            )}
          </Campo>
        )}

        <Campo
          rotulo="Motivo da transferência"
          obrigatorio
          erro={
            motivo.length > 0 && motivo.trim().length < 10
              ? 'Descreva o motivo com ao menos 10 caracteres.'
              : null
          }
          acessorio={<ContadorTexto valor={motivo} minimo={10} maximo={1000} />}
          dica="Fica registrado na timeline de cada lead e não pode ser editado depois."
        >
          {(atributos) => (
            <AreaTexto
              {...atributos}
              rows={3}
              value={motivo}
              temErro={atributos.temErro}
              placeholder="Ex.: Realocação de carteira após saída do atendente."
              onChange={(evento) => setMotivo(evento.target.value)}
            />
          )}
        </Campo>

        <p className="text-xs text-conteudo-fraco">
          Leads em situação terminal não são transferíveis e já ficam fora da seleção. Se algum for
          recusado pelo servidor, ele aparece na lista de ignorados ao final.
        </p>
      </div>
    </Modal>
  )
}
